// Dev-only "view as" override for testing the app as another user.
//
// Sources, highest priority first:
//   1. ?loginAs=<loginName> URL query param (switch users without restarting)
//   2. REACT_APP_IMPERSONATE=<loginName> in Code/.env.development.local (gitignored)
//
// The MSAL token is still yours. User-parameterised endpoints load and save the
// impersonated user's real data. Token-only endpoints either switch to an
// admin variant that takes a user (credentialing) or are blocked via
// assertNotImpersonating (pay stubs, tax documents, profile picture).
//
// Never active outside NODE_ENV === "development": `npm run build` sets
// NODE_ENV to "production" and never loads .env.development.local, so this
// resolves to null and the branch is stripped from production bundles.

const DEV_IMPERSONATION_PARAM = "loginAs";

export const getDevImpersonationLoginName = (): string | null => {
  if (process.env.NODE_ENV !== "development") {
    return null;
  }

  const fromUrl = new URLSearchParams(window.location.search).get(
    DEV_IMPERSONATION_PARAM,
  );
  const loginName = fromUrl?.trim() || process.env.REACT_APP_IMPERSONATE?.trim();

  return loginName || null;
};

/**
 * Block an endpoint that is scoped only to the caller's bearer token (no user
 * parameter exists), so it can't return or change the impersonated user's data.
 * Throws instead of silently acting on the signed-in account.
 * @param feature - Name shown in the error, e.g. "Pay stubs"
 */
export const assertNotImpersonating = (feature: string): void => {
  const loginName = getDevImpersonationLoginName();

  if (loginName) {
    throw new Error(
      `[DEV] ${feature} is unavailable while impersonating "${loginName}": the API only serves the signed-in account for this endpoint.`,
    );
  }
};
